import clsx from 'clsx';
import type { InputHTMLAttributes, ReactNode, SelectHTMLAttributes, TextareaHTMLAttributes } from 'react';
import { AlertCircle, CheckCircle2, Info, Loader2, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { errMsg, errStatus } from './data';

export function Spinner({ label = 'Loading...' }: { label?: string }) {
  return (
    <div className="flex items-center gap-2 text-sm text-slate-500 py-6 justify-center">
      <Loader2 className="animate-spin" size={16} /> {label}
    </div>
  );
}

export function Banner({ tone = 'error', children }: { tone?: 'error' | 'success' | 'info' | 'warn'; children: ReactNode }) {
  if (!children) return null;
  const cls = { error: 'bg-red-50 text-red-800 border-red-200', success: 'bg-green-50 text-green-800 border-green-200', info: 'bg-blue-50 text-blue-800 border-blue-200', warn: 'bg-amber-50 text-amber-800 border-amber-200' }[tone];
  const Icon = tone === 'success' ? CheckCircle2 : tone === 'error' ? AlertCircle : Info;
  return (
    <div role={tone === 'error' ? 'alert' : 'status'} className={clsx('flex gap-2 items-start border rounded-xl px-3 py-2 text-sm', cls)}>
      <Icon size={16} className="mt-0.5 shrink-0" />
      <div>{children}</div>
    </div>
  );
}

export function ErrorBox({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const notImpl = errStatus(error) === 501;
  return (
    <div className="space-y-2">
      <Banner tone={notImpl ? 'warn' : 'error'}>{errMsg(error)}</Banner>
      {onRetry && !notImpl && (
        <Button variant="ghost" onClick={onRetry}>Retry</Button>
      )}
    </div>
  );
}

export function Empty({ children = 'Nothing to show yet.' }: { children?: ReactNode }) {
  return <div className="text-sm text-slate-500 py-8 text-center">{children}</div>;
}

/** Wraps a react-query result with loading / error / empty states. */
export function QueryState({ q, empty, isEmpty, children }: { q: { isLoading: boolean; isError: boolean; error: unknown; refetch: () => unknown }; empty?: ReactNode; isEmpty?: boolean; children: ReactNode }) {
  if (q.isLoading) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} onRetry={() => q.refetch()} />;
  if (isEmpty) return <Empty>{empty}</Empty>;
  return <>{children}</>;
}

export function PageHeader({ title, subtitle, action }: { title: string; subtitle?: string; action?: ReactNode }) {
  return (
    <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
      <div>
        <h1 className="text-xl font-bold text-navy">{title}</h1>
        {subtitle && <p className="text-sm text-slate-500">{subtitle}</p>}
      </div>
      {action}
    </div>
  );
}

const inputCls = 'border rounded-xl px-3 py-2 w-full text-sm bg-white disabled:bg-slate-50';

export function Field({ label, error, hint, children }: { label?: string; error?: string; hint?: string; children: ReactNode }) {
  return (
    <label className="block text-sm">
      {label && <span className="block mb-1 text-slate-600 font-medium">{label}</span>}
      {children}
      {hint && !error && <span className="block text-xs text-slate-400 mt-1">{hint}</span>}
      {error && <span className="block text-xs text-red-600 mt-1">{error}</span>}
    </label>
  );
}

type FP = { label?: string; error?: string; hint?: string };

export function Input({ label, error, hint, className, ...p }: InputHTMLAttributes<HTMLInputElement> & FP) {
  return (
    <Field label={label} error={error} hint={hint}>
      <input className={clsx(inputCls, error && 'border-red-400', className)} {...p} />
    </Field>
  );
}

export function TextArea({ label, error, hint, className, ...p }: TextareaHTMLAttributes<HTMLTextAreaElement> & FP) {
  return (
    <Field label={label} error={error} hint={hint}>
      <textarea rows={3} className={clsx(inputCls, error && 'border-red-400', className)} {...p} />
    </Field>
  );
}

export function Select({ label, error, hint, className, children, ...p }: SelectHTMLAttributes<HTMLSelectElement> & FP) {
  return (
    <Field label={label} error={error} hint={hint}>
      <select className={clsx(inputCls, error && 'border-red-400', className)} {...p}>
        {children}
      </select>
    </Field>
  );
}

export interface Col<T> { key: string; header: string; render?: (row: T, i: number) => ReactNode; className?: string }

export function Table<T>({ cols, rows, rowKey, empty }: { cols: Col<T>[]; rows: T[]; rowKey?: (r: T, i: number) => string | number; empty?: ReactNode }) {
  if (!rows.length) return <Empty>{empty}</Empty>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-slate-500 border-b">
            {cols.map((c) => (
              <th key={c.key} className={clsx('py-2 pr-3 font-medium whitespace-nowrap', c.className)}>{c.header}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={rowKey ? rowKey(r, i) : i} className="border-b last:border-0 align-top">
              {cols.map((c) => (
                <td key={c.key} className={clsx('py-2 pr-3', c.className)}>{c.render ? c.render(r, i) : String((r as any)[c.key] ?? '-')}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  return (
    <div className="fixed inset-0 bg-black/40 grid place-items-center z-50 p-4 overflow-y-auto">
      <div className={clsx('bg-white rounded-card p-6 w-full max-h-[90vh] overflow-y-auto', wide ? 'max-w-3xl' : 'max-w-md')}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold">{title}</h3>
          <button aria-label="Close" onClick={onClose}><X size={18} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Stat({ label, value, tone }: { label: string; value: ReactNode; tone?: string }) {
  return (
    <div className="bg-white rounded-card border border-slate-200 p-4">
      <div className="text-xs text-slate-500">{label}</div>
      <div className={clsx('text-2xl font-bold text-navy', tone)}>{value}</div>
    </div>
  );
}

export function Pager({ meta, onPage }: { meta?: { page: number; last_page: number; total: number } | null; onPage: (p: number) => void }) {
  if (!meta || meta.last_page <= 1) return null;
  return (
    <div className="flex items-center justify-between pt-3 text-sm text-slate-500">
      <span>Page {meta.page} of {meta.last_page} ({meta.total} total)</span>
      <div className="flex gap-2">
        <Button variant="ghost" disabled={meta.page <= 1} onClick={() => onPage(meta.page - 1)}>Previous</Button>
        <Button variant="ghost" disabled={meta.page >= meta.last_page} onClick={() => onPage(meta.page + 1)}>Next</Button>
      </div>
    </div>
  );
}

export function Tabs<K extends string>({ tabs, value, onChange }: { tabs: { key: K; label: string }[]; value: K; onChange: (k: K) => void }) {
  return (
    <div className="flex gap-1 border-b mb-4 flex-wrap">
      {tabs.map((t) => (
        <button key={t.key} onClick={() => onChange(t.key)} className={clsx('px-3 py-2 text-sm -mb-px border-b-2', value === t.key ? 'border-brand-600 text-brand-600 font-semibold' : 'border-transparent text-slate-500')}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

/** Renders arbitrary JSON as nested key/value rows, for response shapes without a fixed UI. */
export function JsonView({ data, depth = 0 }: { data: unknown; depth?: number }) {
  if (data === null || data === undefined) return <span className="text-slate-400">-</span>;
  if (Array.isArray(data)) {
    if (!data.length) return <span className="text-slate-400">none</span>;
    const allObj = data.every((d) => d && typeof d === 'object' && !Array.isArray(d));
    if (allObj && depth < 3) {
      const rows = data as Record<string, unknown>[];
      const keys = Array.from(new Set(rows.flatMap((d) => Object.keys(d)))).filter((k) => !rows.some((d) => d[k] && typeof d[k] === 'object'));
      if (keys.length) {
        return <Table cols={keys.map((k) => ({ key: k, header: k.replace(/_/g, ' ') }))} rows={rows} />;
      }
    }
    return (
      <ul className="list-disc pl-5 space-y-1">
        {data.map((d, i) => (
          <li key={i}><JsonView data={d} depth={depth + 1} /></li>
        ))}
      </ul>
    );
  }
  if (typeof data === 'object') {
    return (
      <dl className="grid grid-cols-[minmax(120px,200px)_1fr] gap-x-3 gap-y-1 text-sm">
        {Object.entries(data as object).map(([k, v]) => (
          <div key={k} className="contents">
            <dt className="text-slate-500">{k.replace(/_/g, ' ')}</dt>
            <dd><JsonView data={v} depth={depth + 1} /></dd>
          </div>
        ))}
      </dl>
    );
  }
  if (typeof data === 'boolean') return <span>{data ? 'Yes' : 'No'}</span>;
  return <span>{String(data)}</span>;
}
